/**
 * Sidebar - Menú de navegación lateral
 * Muestra las opciones de navegación filtradas por rol
 */
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Collapse,
} from '@mui/material';
import {
  Dashboard,
  Receipt,
  AccountBalance,
  Assessment,
  Folder,
  History,
  ExpandLess,
  ExpandMore,
  Add,
  List as ListIcon,
  Paid,
  Warning,
  CalendarMonth,
  Business,
  Category,
  People,
  Settings,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Configuración del menú de navegación
 * Cada item puede tener permisos por rol
 */
const menuConfig = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: Dashboard,
    path: '/dashboard',
    roles: ['administrador', 'cajero', 'contador', 'auditor'],
  },
  {
    id: 'cheques',
    label: 'Cheques',
    icon: Receipt,
    roles: ['administrador', 'cajero', 'contador', 'auditor'],
    children: [
      {
        id: 'cheques-list',
        label: 'Listado de Cheques',
        icon: ListIcon,
        path: '/cheques/lista',
        roles: ['administrador', 'cajero', 'contador', 'auditor'],
      },
      {
        id: 'cheques-nuevo',
        label: 'Registrar Cheque',
        icon: Add,
        path: '/cheques/nuevo',
        roles: ['administrador', 'cajero', 'contador'],
      },
      {
        id: 'cheques-pagados',
        label: 'Cheques Pagados',
        icon: Paid,
        path: '/cheques/pagados',
        roles: ['administrador', 'cajero', 'contador', 'auditor'],
      },
      {
        id: 'cheques-novedades',
        label: 'Novedades',
        icon: Warning,
        path: '/cheques/novedades',
        roles: ['administrador', 'cajero', 'contador'],
      },
      {
        id: 'cheques-cambio-fecha',
        label: 'Cambios de Fecha',
        icon: CalendarMonth,
        path: '/cheques/cambio-fecha',
        roles: ['administrador', 'contador'],
      },
    ],
  },
  {
    id: 'depositos',
    label: 'Depósitos',
    icon: AccountBalance,
    roles: ['administrador', 'contador', 'auditor'],
    children: [
      {
        id: 'depositos-list',
        label: 'Listado de Depósitos',
        icon: ListIcon,
        path: '/depositos/lista',
        roles: ['administrador', 'contador', 'auditor'],
      },
      {
        id: 'depositos-nuevo',
        label: 'Crear Depósito',
        icon: Add,
        path: '/depositos/nuevo',
        roles: ['administrador', 'contador'],
      },
    ],
  },
  {
    id: 'reportes',
    label: 'Reportes',
    icon: Assessment,
    roles: ['administrador', 'cajero', 'contador', 'auditor'],
    children: [
      {
        id: 'reportes-diario',
        label: 'Reporte Diario',
        icon: Assessment,
        path: '/reportes/diario',
        roles: ['administrador', 'contador', 'auditor'],
      },
      {
        id: 'reportes-semanal',
        label: 'Reporte Semanal',
        icon: CalendarMonth,
        path: '/reportes/semanal',
        roles: ['administrador', 'contador', 'auditor'],
      },
      {
        id: 'reportes-pendientes',
        label: 'Cheques Pendientes',
        icon: Warning,
        path: '/reportes/pendientes',
        roles: ['administrador', 'contador', 'auditor'],
      },
      {
        id: 'reportes-calendario',
        label: 'Calendario Posfechados',
        icon: CalendarMonth,
        path: '/reportes/calendario',
        roles: ['administrador', 'contador', 'auditor'],
      },
      {
        id: 'reportes-clientes',
        label: 'Cheques por Cliente',
        icon: PeopleAltIcon,
        path: '/reportes/clientes',
        roles: ['administrador', 'cajero', 'contador', 'auditor'],
      },
    ],
  },
  {
    id: 'catalogos',
    label: 'Catálogos',
    icon: Folder,
    roles: ['administrador', 'contador'],
    children: [
      {
        id: 'catalogos-clientes',
        label: 'Clientes',
        icon: People,
        path: '/catalogos/clientes',
        roles: ['administrador', 'contador'],
      },
      {
        id: 'catalogos-bancos',
        label: 'Bancos',
        icon: Business,
        path: '/catalogos/bancos',
        roles: ['administrador', 'contador'],
      },
      {
        id: 'catalogos-cuentas',
        label: 'Cuentas Bancarias',
        icon: AccountBalance,
        path: '/catalogos/cuentas',
        roles: ['administrador'],
      },
      {
        id: 'catalogos-novedades',
        label: 'Tipos de Novedad',
        icon: Category,
        path: '/catalogos/novedades',
        roles: ['administrador', 'contador'],
      },
    ],
  },
  {
    id: 'auditoria',
    label: 'Auditoría',
    icon: History,
    path: '/auditoria',
    roles: ['administrador', 'auditor'],
  },
  {
    id: 'administracion',
    label: 'Administración',
    icon: Settings,
    roles: ['administrador'],
    children: [
      {
        id: 'empresas',
        label: 'Empresas',
        icon: Business,
        path: '/administracion/empresas',
        roles: ['administrador'],
      },
      {
        id: 'usuarios',
        label: 'Usuarios',
        icon: People,
        path: '/catalogos/usuarios',
        roles: ['administrador'],
      },
    ],
  },
];

/**
 * @component Sidebar
 * @description Menú lateral con navegación y filtrado por roles
 */
const Sidebar = ({ open, onClose, drawerWidth, isMobile }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState({});

  /**
   * Verificar si el usuario tiene permiso para ver un item
   */
  const hasPermission = (item) => {
    if (!item.roles) return true;
    return item.roles.includes(user?.rol);
  };

  /**
   * Verificar si la ruta está activa
   */
  const isActive = (path) => {
    return location.pathname === path;
  };

  /**
   * Manejar click en item del menú
   */
  const handleItemClick = (item) => {
    if (item.children) {
      // Toggle expand/collapse
      setExpandedItems({
        ...expandedItems,
        [item.id]: !expandedItems[item.id],
      });
    } else if (item.path) {
      // Navegar
      navigate(item.path);
      if (isMobile) {
        onClose();
      }
    }
  };

  /**
   * Renderizar un item del menú
   */
  const renderMenuItem = (item, level = 0) => {
    if (!hasPermission(item)) return null;

    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];
    const active = item.path && isActive(item.path);

    return (
      <Box key={item.id}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={active}
            sx={{
              pl: 2 + level * 2,
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: level > 0 ? '0.875rem' : '0.95rem',
                fontWeight: active ? 600 : 400,
              }}
            />
            {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>

        {/* Items hijos */}
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  };

  const drawerContent = (
    <Box sx={{ overflow: 'auto', mt: 1 }}>
      {/* Header del sidebar */}
      <Box sx={{ px: 2, py: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Menú
        </Typography>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Lista de menú */}
      <List>
        {menuConfig.map((item) => renderMenuItem(item))}
      </List>
    </Box>
  );

  return (
    <>
      {/* Drawer temporal para móvil */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true, // Mejor rendimiento en móvil
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              mt: { xs: 7, sm: 8 },
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        // Drawer permanente para desktop
        <Drawer
          variant="persistent"
          open={open}
          sx={{
            width: open ? drawerWidth : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              mt: { xs: 7, sm: 8 },
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </>
  );
};

Sidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  drawerWidth: PropTypes.number.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default Sidebar;
